// src/layout/Topbar.tsx
import React from "react";
import { FiMoon, FiSun } from "react-icons/fi";
import { MdLogout, MdPerson, MdRadar } from "react-icons/md";
import { useTheme } from "../providers/ThemeProvider";
import { useAuthStore } from "../stores/authStore";
import { signOutApp } from "../firebase/auth";
import { useNavigate } from "react-router-dom";
import { MobileMenuButton, MobileSidebar } from "./Sidebar";

function getInitials(name: string | null, email: string | null): string {
  const n = (name ?? "").trim();
  if (n) {
    const parts = n.split(/\s+/);
    const a = parts[0]?.[0] ?? "";
    const b = parts[parts.length - 1]?.[0] ?? "";
    return `${a}${b || ""}`.toUpperCase() || "U";
  }
  const mail = (email ?? "").trim();
  return mail ? mail[0]!.toUpperCase() : "U";
}

export default function Topbar(): React.ReactElement {
  const { theme, toggle } = useTheme();
  const auth = useAuthStore((s) => s.state);
  const navigate = useNavigate();

  const displayName: string =
    auth.profile?.fullName ??
    auth.user?.displayName ??
    auth.user?.email ??
    "Usuario";

  const photoURL: string | null =
    auth.profile?.photoURL ?? auth.user?.photoURL ?? null;

  // Popover de usuario
  const [open, setOpen] = React.useState<boolean>(false);
  const popRef = React.useRef<HTMLDivElement | null>(null);

  // Drawer móvil
  const [mobileOpen, setMobileOpen] = React.useState<boolean>(false);

  React.useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!open) return;
      const node = popRef.current;
      if (node && !node.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const signOut = async () => {
    try {
      await signOutApp();
    } catch (err) {
      alert((err as Error).message);
    } finally {
      navigate("/login", { replace: true });
    }
  };

  const loading = auth.status === "loading";

  return (
    <>
      <header
        className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-surface px-4 shadow-soft backdrop-blur supports-[backdrop-filter]:bg-surface/85 bg-surface"
        style={{
          backgroundImage:
            "radial-gradient(500px 200px at 0% -40%, color-mix(in oklab, var(--color-primary) 12%, transparent) 0%, transparent 70%), radial-gradient(400px 160px at 110% 120%, color-mix(in oklab, var(--color-secondary) 12%, transparent) 0%, transparent 70%)",
        }}
      >
        {/* Marca + botón hamburguesa (solo móvil) */}
        <div className="flex items-center gap-2 font-semibold">
          {/* botón menú: visible en móvil, oculto en md+ (el propio componente aplica md:hidden) */}
          <MobileMenuButton onClick={() => setMobileOpen(true)} />

          <span
            className="grid h-8 w-8 place-items-center rounded-xl"
            style={{
              background:
                "color-mix(in oklab, var(--color-primary) 16%, transparent)",
            }}
          >
            <MdRadar
              className="text-lg"
              style={{ color: "var(--color-primary)" }}
            />
          </span>
          <span className="tracking-tight">Radar4 Dashboard</span>
        </div>

        {/* Acciones */}
        <div className="flex items-center gap-2">
          {/* Toggle tema (tonal) */}
          <button
            type="button"
            className="rounded-lg border border-surface bg-secondaryContainer px-3 py-1 text-onSecondaryContainer hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary/40"
            onClick={toggle}
            aria-label="Cambiar tema"
            title="Cambiar tema"
          >
            {theme === "light" ? <FiMoon /> : <FiSun />}
          </button>

          {/* Usuario */}
          <div className="relative" ref={popRef}>
            <button
              type="button"
              className="flex items-center gap-2 rounded-lg border border-surface bg-surface/90 px-2 py-1 hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary/40"
              onClick={() => setOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={open}
              aria-label="Menú de usuario"
            >
              {loading ? (
                <>
                  <span className="inline-block h-7 w-7 animate-pulse rounded-full bg-black/10 dark:bg-white/10" />
                  <span className="inline-block h-4 w-24 animate-pulse rounded bg-black/10 dark:bg-white/10" />
                </>
              ) : (
                <Avatar
                  name={displayName}
                  email={auth.user?.email ?? null}
                  photoURL={photoURL}
                />
              )}
            </button>

            {open && !loading && (
              <div
                role="menu"
                aria-label="Opciones de usuario"
                className="absolute right-0 mt-2 w-64 overflow-hidden rounded-2xl border border-surface bg-surface shadow-soft"
              >
                <div className="flex items-center gap-3 bg-secondaryContainer px-3 py-3 text-onSecondaryContainer">
                  <Avatar
                    name={displayName}
                    email={auth.user?.email ?? null}
                    photoURL={photoURL}
                    size={36}
                  />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">
                      {displayName}
                    </div>
                    {auth.user?.email ? (
                      <div className="truncate text-xs opacity-80">
                        {auth.user.email}
                      </div>
                    ) : null}
                  </div>
                </div>

                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-primary/10 disabled:opacity-60"
                  disabled
                >
                  <MdPerson className="opacity-70" /> Perfil (pronto)
                </button>

                <hr className="border-surface" />

                <button
                  type="button"
                  onClick={signOut}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-500/10"
                >
                  <MdLogout /> Cerrar sesión
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Drawer del sidebar para móviles */}
      <MobileSidebar open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </>
  );
}

/** Avatar redondo: foto o iniciales */
function Avatar({
  name,
  email,
  photoURL,
  size = 28,
}: {
  name: string | null;
  email: string | null;
  photoURL: string | null;
  size?: number;
}): React.ReactElement {
  const initials = getInitials(name, email);
  const px = `${size}px`;

  if (photoURL && photoURL.length > 0) {
    return (
      <img
        src={photoURL}
        alt={name ?? "Usuario"}
        width={size}
        height={size}
        className="rounded-full object-cover"
        style={{ width: px, height: px }}
      />
    );
  }

  return (
    <div
      aria-hidden
      className="grid place-items-center rounded-full text-primary"
      style={{
        width: px,
        height: px,
        background:
          "color-mix(in oklab, var(--color-primary) 18%, transparent)",
        fontSize: Math.max(10, Math.floor(size * 0.45)),
      }}
      title={name ?? email ?? "Usuario"}
    >
      {initials}
    </div>
  );
}
